import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  Card,
  CardContent,
  CardActions,
  Grid2 as Grid,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Article as ArticleIcon,
  FileCopy as FileCopyIcon,
  Link as LinkIcon,
  Person as PersonIcon,
  LocalHospital as ClinicIcon,
} from '@mui/icons-material';
import { usePages } from '../hooks/queries';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';

// URL base das páginas públicas - usa variável de ambiente
const PAGES_BASE_URL = import.meta.env.VITE_PAGES_URL || 'https://pages.agendario.com.br';

const PAGE_TYPE_CONFIG = {
  link_in_bio: { label: 'Link in Bio', icon: <LinkIcon sx={{ fontSize: 14 }} />, color: 'primary' },
  professional: { label: 'Profissional', icon: <PersonIcon sx={{ fontSize: 14 }} />, color: 'secondary' },
  clinic:       { label: 'Clínica', icon: <ClinicIcon sx={{ fontSize: 14 }} />, color: 'info' },
};

function PageTypeChip({ type, variant = 'outlined', size = 'small' }) {
  const config = PAGE_TYPE_CONFIG[type] || { label: type, icon: null, color: 'default' };
  return (
    <Chip
      label={config.label}
      icon={config.icon}
      color={config.color}
      variant={variant}
      size={size}
    />
  );
}
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import PageTypeSelector from '../components/pages/PageTypeSelector';

import { useContextualHint } from '../hooks/useContextualHint';
import PageLayout from '../components/ui/PageLayout';
import StandardTable from '../components/ui/StandardTable';

export default function PagesPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const hint = useContextualHint('pages', {});

  // React Query Hooks
  const { items: pages, loading, error, reload, create, update, deleteItem } = usePages();

  // Dialog States
  const typeSelector = useDialog();
  const deleteDialog = useDialog();

  // Snackbar State
  const { snackbar, showSnackbar, hideSnackbar, showSuccess, showError } = useSnackbar();

  // Handlers
  const handleCreateNew = () => {
    typeSelector.handleOpen();
  };

  const handlePreview = (page) => {
    // Abrir em nova aba a página pública
    window.open(`/pages/${page.shortId}`, '_blank');
  };

  const handleCopyUrl = (page) => {
    const url = `${PAGES_BASE_URL}/${page.slug}-${page.shortId}`;
    navigator.clipboard.writeText(url).then(() => {
      showSuccess('URL copiada para a área de transferência!');
    }).catch(() => {
      showError('Erro ao copiar URL');
    });
  };

  // Loading/Error States
  if (loading) return <LoadingState message="Carregando páginas..." />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const tableColumns = [
    { id: 'title', label: 'Título' },
    { id: 'url', label: 'URL Pública' },
    { id: 'type', label: 'Tipo' },
    { id: 'actions', label: 'Ações', align: 'right' },
  ];

  return (
    <PageLayout
      themeKey="pages"
      actionLabel="Nova Página"
      onAction={handleCreateNew}
      hint={hint}
    >
      {/* Empty State */}
      {pages.length === 0 ? (
        <EmptyState
          icon={ArticleIcon}
          title="Nenhuma página cadastrada"
          message="Clique em 'Nova Página' para criar sua primeira página Link-in-Bio."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
            >
              Nova Página
            </Button>
          }
        />
      ) : isMobile ? (
        // MOBILE: Cards Grid
        <Grid container spacing={2}>
          {pages.map((page) => (
            <Grid size={{ xs: 12 }} key={page.id}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderLeft: '3px solid', borderLeftColor: 'primary.main' }}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                      <Typography variant="h6" fontWeight="bold">
                        {page.title}
                      </Typography>
                      {page.isPublished && (
                        <Chip label="Publicada" size="small" color="success" variant="outlined" />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box
                        component="a"
                        href={`${PAGES_BASE_URL}/${page.slug}-${page.shortId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                          color: '#1976d2',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {PAGES_BASE_URL}/{page.slug}-{page.shortId}
                      </Box>
                      <Tooltip title="Copiar URL">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyUrl(page)}
                          sx={{ p: 0 }}
                        >
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <PageTypeChip type={page.type} variant="outlined" />
                  </Box>
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2, flexWrap: 'wrap', gap: 1 }}>
                  {page.isPublished && (
                    <Tooltip title="Preview">
                      <IconButton
                        size="small"
                        onClick={() => handlePreview(page)}
                        color="primary"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Editar">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/pages/edit/${page.id}`)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton
                      size="small"
                      onClick={() => deleteDialog.handleOpen(page)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // DESKTOP: Table
        <StandardTable columns={tableColumns}>
          {pages.map((page) => (
                <TableRow key={page.id}>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {page.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        component="a"
                        href={`${PAGES_BASE_URL}/${page.slug}-${page.shortId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          color: '#1976d2',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {PAGES_BASE_URL}/{page.slug}-{page.shortId}
                      </Box>
                      <Tooltip title="Copiar URL completa">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyUrl(page)}
                          sx={{ p: 0.5 }}
                        >
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <PageTypeChip type={page.type} variant="filled" />
                  </TableCell>
                  <TableCell align="right">
                    {page.isPublished && (
                      <Tooltip title="Preview">
                        <IconButton
                          size="small"
                          onClick={() => handlePreview(page)}
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/pages/edit/${page.id}`)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        onClick={() => deleteDialog.handleOpen(page)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
          ))}
        </StandardTable>
      )}

      {/* Page Type Selector Modal */}
      <PageTypeSelector
        open={typeSelector.open}
        onClose={typeSelector.handleClose}
      />

      {/* Delete Confirmation Dialog */}
      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDeleted={() => {
          deleteDialog.handleClose();
          showSuccess('Excluído com sucesso!');
          reload();
        }}
        deletePath={`/pages/${deleteDialog.data?.id}`}
        entityName={deleteDialog.data?.title}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
}
